import React from 'react';
import loadable from '@loadable/component';
import './App.css';
import { PersistGate } from 'redux-persist/integration/react';
import configureStore from './store';
import { Provider } from 'react-redux';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import LoadableSpin from './components/common/LoadableSpin';
import { setRfqAuthToken } from './util/rfqApi';
// POC dev-only: floating "Load sample rates" trigger (renders only when
// REACT_APP_USE_LOCAL_RATES === 'yes'); lets the results screen + Create-RFQ
// entry point be reached offline.
import DevLoadResults from './components/dev/DevLoadResults';

// code splitting
// Home page
const Home = loadable(() => import('./components/Home'), {
  fallback: <LoadableSpin />,
});


// Landing page
const LandingPage = loadable(
  () => import('./components/landing-page/LandingPage'),
  {
    fallback: <span></span>,
  }
);

// dashboard page
const DashboardPage = loadable(
  () => import('./components/dashboard/Dashboard'),
  {
    fallback: <span></span>,
  }
);

// Agent page
const AgentPage = loadable(
  () => import('./components/agent/AgentPage'),
  {
    fallback: <span></span>,
  }
);

// RFQ creator page (/rfq/new) — routed RFQ creation, replaces the thin modal.
const RfqCreatorPage = loadable(() => import('./components/rfq/RfqCreatorPage'), {
  fallback: <LoadableSpin />,
});

// RFQ customer offer-view (/rfq/:id/offers) — the presented options + accept (#78 W5).
const RfqOffersPage = loadable(() => import('./components/rfq/RfqOffersPage'), {
  fallback: <LoadableSpin />,
});

// RFQ booking handoff (/rfq/:rfqId/quote/:quoteId/book) — the Portal "Accept &
// create booking" button lands here; seeds the agreed quote into the real
// FInalView booking confirmation (#188).
const RfqBookingPage = loadable(() => import('./components/rfq/RfqBookingPage'), {
  fallback: <LoadableSpin />,
});

// for 404 page not found
const NotFound = loadable(() => import('./components/not-found/NotFound'), {
  fallback: <LoadableSpin />,
});

//for testing
// const Test = loadable(() => import('./components/testComp/TestContainer'), {
//   fallback: <div style={{ textAlign: 'center' }}>Loading...</div>,
// });

//for Hot reloading
const { store, persistor } = configureStore();

// Keep the RFQ orchestrator client's caller JWT in sync with the store, in ONE
// place (#rfq-caller-auth). Mirrors the canonical read used across actions
// (route_actions `searchPurchaseOrders`): the logged-in iframe session keeps its
// token on CLIENT_TOKEN, the anonymous bootstrap on JWT_TOKEN; guests have
// neither → null → no Authorization header is sent. Subscribing here covers every
// path that sets the token (initAuth, JWT bootstrap, the dev login toggle).
//
// The anonymous bootstrap no longer holds a real JWT — /api/betty-login returns the
// opaque 'pf-anon' placeholder (swapped for a real JWT inside the Netlify proxies).
// The orchestrator is NOT behind those proxies, so the placeholder must map to
// guest/no-Authorization here. Keep in sync with netlify/functions/lib/bettyAnon.mjs.
const ANON_TOKEN_PLACEHOLDER = 'pf-anon';
const syncRfqAuthToken = () => {
  const route = (store.getState() || {}).route || {};
  const isUserPresent = route.USER_DETAILS && route.USER_DETAILS.webuser && route.USER_DETAILS.webuser.userid != null;
  const jwt = isUserPresent
    ? route.CLIENT_TOKEN && route.CLIENT_TOKEN.JWT_TOKEN
    : route.JWT_TOKEN && route.JWT_TOKEN.JWT_TOKEN;
  setRfqAuthToken(jwt === ANON_TOKEN_PLACEHOLDER ? null : jwt);
};
syncRfqAuthToken();
store.subscribe(syncRfqAuthToken);

/**
 * Main container Component of the web app
 * @component
 */

const App = () => {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <Router>
          <div className="app ">
            <DevLoadResults />
            <div className="container">
              <Switch>

                <Route exact path="/" component={Home} />
                <Route exact path="/landing-page" component={LandingPage} />
                <Route exact path="/agent" component={AgentPage} />
                <Route exact path="/dashboard" component={DashboardPage} />
                <Route exact path="/rfq/new" component={RfqCreatorPage} />
                <Route exact path="/rfq/:id/offers" component={RfqOffersPage} />
                <Route exact path="/rfq/:rfqId/quote/:quoteId/book" component={RfqBookingPage} />
                <Route exact path="/*" component={NotFound} />
              </Switch>
            </div>
          </div>
        </Router>
      </PersistGate>
    </Provider>
  );
};

export default App;
